import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "carousel",
    "slide",
    "indicator",
    "prevButton",
    "nextButton",
  ];
  static values = {
    currentIndex: Number,
    interval: { type: Number, default: 6000 },
    autoplay: { type: Boolean, default: true }
  };

  connect() {
    this.currentIndexValue = 0;
    this.totalSlides = this.slideTargets.length;

    // スワイプ用の変数を初期化
    this.startX = 0;
    this.currentX = 0;
    this.isDragging = false;

    // イベントハンドラーをバインドして保存（後でremoveEventListenerで使用）
    this.boundHandlers = {
      touchStart: this.handleTouchStart.bind(this),
      touchMove: this.handleTouchMove.bind(this),
      touchEnd: this.handleTouchEnd.bind(this),
      mouseDown: this.handleMouseDown.bind(this),
      mouseMove: this.handleMouseMove.bind(this),
      mouseUp: this.handleMouseUp.bind(this),
      mouseLeave: this.handleMouseUp.bind(this),
      mouseEnter: this.handleMouseEnter.bind(this),
      mouseLeaveHover: this.handleMouseLeave.bind(this)
    };

    // スライドが1枚以下の場合はコントロールを非表示
    if (this.totalSlides <= 1) {
      this.hideControls();
      return;
    }

    this.updateCarousel();
    this.updateIndicators();
    this.updateButtonStates();
    this.setupTouchEvents();

    // 自動再生を開始
    if (this.autoplayValue && this.totalSlides > 1) {
      this.startAutoplay();
      this.setupHoverEvents();
    }
  }

  setupTouchEvents() {
    const carousel = this.carouselTarget;

    // 既存のイベントリスナーを削除（重複防止）
    this.removeTouchEvents();

    // タッチイベント
    carousel.addEventListener("touchstart", this.boundHandlers.touchStart, {
      passive: true,
    });
    carousel.addEventListener("touchmove", this.boundHandlers.touchMove, {
      passive: false,
    });
    carousel.addEventListener("touchend", this.boundHandlers.touchEnd, {
      passive: true,
    });

    // マウスイベント（デスクトップでのドラッグ対応）
    carousel.addEventListener("mousedown", this.boundHandlers.mouseDown);
    carousel.addEventListener("mousemove", this.boundHandlers.mouseMove);
    carousel.addEventListener("mouseup", this.boundHandlers.mouseUp);
    carousel.addEventListener("mouseleave", this.boundHandlers.mouseLeave);
  }

  removeTouchEvents() {
    if (!this.boundHandlers) return;

    const carousel = this.carouselTarget;

    // タッチイベントの削除
    carousel.removeEventListener("touchstart", this.boundHandlers.touchStart);
    carousel.removeEventListener("touchmove", this.boundHandlers.touchMove);
    carousel.removeEventListener("touchend", this.boundHandlers.touchEnd);

    // マウスイベントの削除
    carousel.removeEventListener("mousedown", this.boundHandlers.mouseDown);
    carousel.removeEventListener("mousemove", this.boundHandlers.mouseMove);
    carousel.removeEventListener("mouseup", this.boundHandlers.mouseUp);
    carousel.removeEventListener("mouseleave", this.boundHandlers.mouseLeave);
  }

  previous() {
    // 無限ループ: 最初のスライドの場合は最後に移動
    if (this.currentIndexValue <= 0) {
      this.currentIndexValue = this.totalSlides - 1;
    } else {
      this.currentIndexValue--;
    }
    this.updateCarousel();
    this.updateIndicators();
    this.updateButtonStates();
    this.pauseOnInteraction();
  }

  next() {
    // 無限ループ: 最後のスライドの場合は最初に移動
    if (this.currentIndexValue >= this.totalSlides - 1) {
      this.currentIndexValue = 0;
    } else {
      this.currentIndexValue++;
    }
    this.updateCarousel();
    this.updateIndicators();
    this.updateButtonStates();
    this.pauseOnInteraction();
  }

  goToSlide(event) {
    const index = parseInt(event.currentTarget.dataset.index);
    this.currentIndexValue = index;
    this.updateCarousel();
    this.updateIndicators();
    this.updateButtonStates();
    this.pauseOnInteraction();
  }

  // 自動再生関連メソッド
  startAutoplay() {
    this.stopAutoplay(); // 既存のタイマーをクリア
    this.timer = setInterval(() => {
      this.next();
    }, this.intervalValue);
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  pauseOnInteraction() {
    this.stopAutoplay();
    // 5秒後に自動再生を再開
    setTimeout(() => {
      if (this.autoplayValue && this.totalSlides > 1) {
        this.startAutoplay();
      }
    }, 5000);
  }

  setupHoverEvents() {
    const element = this.element;

    // 既存のホバーイベントを削除（重複防止）
    this.removeHoverEvents();

    // ホバー時は自動再生を停止
    element.addEventListener('mouseenter', this.boundHandlers.mouseEnter);

    // ホバー解除時は自動再生を再開
    element.addEventListener('mouseleave', this.boundHandlers.mouseLeaveHover);
  }

  removeHoverEvents() {
    if (!this.boundHandlers) return;

    const element = this.element;
    element.removeEventListener('mouseenter', this.boundHandlers.mouseEnter);
    element.removeEventListener('mouseleave', this.boundHandlers.mouseLeaveHover);
  }

  handleMouseEnter() {
    this.stopAutoplay();
  }

  handleMouseLeave() {
    if (this.autoplayValue && this.totalSlides > 1) {
      this.startAutoplay();
    }
  }

  disconnect() {
    this.stopAutoplay();
    this.removeTouchEvents();
    this.removeHoverEvents();
  }

  updateCarousel() {
    const translateX = -this.currentIndexValue * 100;
    this.carouselTarget.style.transform = `translateX(${translateX}%)`;
  }

  updateIndicators() {
    this.indicatorTargets.forEach((indicator, index) => {
      if (index === this.currentIndexValue) {
        indicator.classList.add("bg-primary");
        indicator.classList.remove("bg-gray-300");
      } else {
        indicator.classList.add("bg-gray-300");
        indicator.classList.remove("bg-primary");
      }
    });
  }

  updateButtonStates() {
    // 無限ループの場合は常にボタンを表示
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.style.display = "block";
    }

    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.style.display = "block";
    }
  }

  hideControls() {
    // インジケーターを非表示
    this.indicatorTargets.forEach((indicator) => {
      indicator.style.display = "none";
    });

    // ボタンを非表示
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.style.display = "none";
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.style.display = "none";
    }
  }

  // タッチイベントハンドラー
  handleTouchStart(event) {
    this.startX = event.touches[0].clientX;
    this.isDragging = true;
  }

  handleTouchMove(event) {
    if (!this.isDragging) return;

    this.currentX = event.touches[0].clientX;
    // スクロールを防ぐ
    if (Math.abs(this.currentX - this.startX) > 10) {
      event.preventDefault();
    }
  }

  handleTouchEnd(event) {
    if (!this.isDragging) return;

    const diffX = this.startX - this.currentX;
    const threshold = 50; // スワイプと判定する最小距離

    if (Math.abs(diffX) > threshold) {
      if (diffX > 0) {
        // 左スワイプ（次へ）
        this.next();
      } else {
        // 右スワイプ（前へ）
        this.previous();
      }
      // スワイプ操作時も自動再生を一時停止
      this.pauseOnInteraction();
    }

    this.isDragging = false;
  }

  // マウスイベントハンドラー（デスクトップ対応）
  handleMouseDown(event) {
    this.startX = event.clientX;
    this.isDragging = true;
    event.preventDefault(); // テキスト選択を防ぐ
  }

  handleMouseMove(event) {
    if (!this.isDragging) return;

    this.currentX = event.clientX;
  }

  handleMouseUp(event) {
    if (!this.isDragging) return;

    const diffX = this.startX - this.currentX;
    const threshold = 50;

    if (Math.abs(diffX) > threshold) {
      if (diffX > 0) {
        this.next();
      } else {
        this.previous();
      }
      // ドラッグ操作時も自動再生を一時停止
      this.pauseOnInteraction();
    }

    this.isDragging = false;
  }
}
